/**
 * Durable outbox for writes made while offline (RSVP, check-in, comments), stored in the base Dexie
 * DB ('kgr', table outbox) so they survive the app being closed. Rules in outboxLogic.ts: FIFO per
 * device, tagged with the user, collapse by key keeping the earliest position, network/5xx → retry
 * later, 401 → stop, other 4xx → drop + report.
 *
 * Features register a handler per kind (api/calendar.ts): how to send (prepare), what to do with the
 * server's answer (onSent), how to roll back (onRejected) and how to re-apply optimistic state after
 * a restart (onRestore). Only kinds with a handler are flushed — other features' rows are left alone.
 *
 * Flushed on start, 'online', visibility, every 60 s while visible, and right after every enqueue.
 */
import type { MeDTO } from '../../shared/dto/auth';
import { uuid } from '../../shared/ids';
import { api } from '../api/client';
import { queryClient } from '../api/queryClient';
import { localDb, type OutboxItem } from '../db/dexie';
import { classifyFailure, flushOrder, planCollapse, retryDelayMs } from './outboxLogic';
import { withReopen } from '../lib/idb';

export interface OutboxHandler {
  /** Body actually sent (default: the stored body). */
  prepare?(item: OutboxItem): unknown;
  /** The server accepted the write. */
  onSent?(item: OutboxItem, response: unknown): void;
  /**
   * The server refused the write (4xx other than 401); it was dropped from the queue. `immediate`
   * = the enqueue() call is still waiting for this answer (the caller reports the error itself).
   */
  onRejected?(item: OutboxItem, error: unknown, immediate: boolean): void;
  /** A queued item found after a restart: re-apply its optimistic state. */
  onRestore?(item: OutboxItem): void;
  /** The queue was wiped (logout / user switch): forget optimistic state kept for queued writes. */
  onReset?(): void;
}

export type EnqueueOutcome = { status: 'sent'; response: unknown } | { status: 'queued' } | { status: 'rejected'; error: unknown };

export interface EnqueueRequest {
  /** Op id (default: a new UUID). Lets the caller tie optimistic state to this write. */
  id?: string;
  kind: string;
  key: string;
  method: OutboxItem['method'];
  path: string;
  body: unknown;
  /** Combines the body of a queued write with the same key and the new one (default: new wins). */
  merge?: (prev: unknown, next: unknown) => unknown;
}

const handlers = new Map<string, OutboxHandler>();
const waiters = new Map<string, ((o: EnqueueOutcome) => void)[]>();
/** Queued keys of the signed-in user → op id (synchronous isQueued() for render code). */
let mirror = new Map<string, string>();
const listeners = new Set<() => void>();
const restored = new Set<string>();
let started = false;
let flushing: Promise<void> | null = null;
let again = false;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let failures = 0;

function currentUserId(): string | null {
  return queryClient.getQueryData<MeDTO | null>(['me'])?.id ?? null;
}

function notify(): void {
  for (const fn of listeners) fn();
}

/** Re-renders on queue changes (e.g. "bíður sendingar" markers). */
export function subscribeOutbox(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function isQueued(key: string): boolean {
  return mirror.has(key);
}

/**
 * Drops a key from the mirror right away when this op left the queue, BEFORE the handlers run: their
 * cache updates re-render components that read isQueued() during render.
 */
function unmirror(item: OutboxItem): void {
  if (mirror.get(item.key) !== item.id) return;
  mirror.delete(item.key);
  notify();
}

function addWaiter(id: string, fn: (o: EnqueueOutcome) => void): void {
  const list = waiters.get(id);
  if (list) list.push(fn);
  else waiters.set(id, [fn]);
}

function resolveWaiters(id: string, o: EnqueueOutcome): void {
  const list = waiters.get(id);
  if (!list) return;
  waiters.delete(id);
  for (const fn of list) fn(o);
}

function resolveAllQueued(): void {
  for (const id of [...waiters.keys()]) resolveWaiters(id, { status: 'queued' });
}

async function userItems(userId: string): Promise<OutboxItem[]> {
  const db = localDb();
  return withReopen(db, () => db.outbox.where('userId').equals(userId).toArray());
}

async function refreshMirror(userId: string | null): Promise<void> {
  const next = new Map<string, string>();
  if (userId) {
    try {
      for (const it of flushOrder(await userItems(userId), userId, new Set(handlers.keys()))) next.set(it.key, it.id);
    } catch {
      /* IndexedDB unavailable */
    }
  }
  mirror = next;
  notify();
}

/** Registers how a kind is sent and rolled back. Re-applies queued items of that kind after a restart. */
export function registerOutboxHandler(kind: string, h: OutboxHandler): void {
  handlers.set(kind, h);
  if (started) void restore();
}

async function restore(): Promise<void> {
  const userId = currentUserId();
  if (!userId) return;
  let items: OutboxItem[];
  try {
    items = flushOrder(await userItems(userId), userId, new Set(handlers.keys()));
  } catch {
    return;
  }
  // Mirror first: onRestore re-renders the affected screens, which ask isQueued() while rendering.
  for (const it of items) mirror.set(it.key, it.id);
  if (items.length > 0) notify();
  for (const it of items) {
    if (restored.has(it.id)) continue;
    restored.add(it.id);
    try {
      handlers.get(it.kind)?.onRestore?.(it);
    } catch (e) {
      console.warn('outbox restore failed', e);
    }
  }
  await refreshMirror(userId);
}

/**
 * Queues a write and flushes. Resolves with the outcome of the first attempt: 'sent' (with the
 * response), 'queued' (offline / server unavailable — kept and retried later) or 'rejected'.
 */
export async function enqueue(req: EnqueueRequest): Promise<EnqueueOutcome> {
  const userId = currentUserId();
  if (!userId) throw new Error('not signed in');
  const id = req.id ?? uuid();
  const db = localDb();
  let previousId: string | null = null;
  try {
    await withReopen(db, () => db.transaction('rw', db.outbox, async () => {
      const same = await db.outbox.where('key').equals(req.key).toArray();
      const plan = planCollapse(
        same,
        { id, userId, kind: req.kind, key: req.key, method: req.method, path: req.path, body: req.body, createdAt: Date.now() },
        req.merge,
      );
      if (plan.action === 'add') await db.outbox.add(plan.item as OutboxItem);
      else {
        await db.outbox.put(plan.item);
        previousId = plan.previousId;
      }
    }));
  } catch (e) {
    console.warn('outbox unavailable, sending directly', e);
    return sendDirect(id, userId, req);
  }
  restored.add(id);
  const outcome = new Promise<EnqueueOutcome>((resolve) => addWaiter(id, resolve));
  if (previousId) {
    // The earlier write was replaced: whoever waits for it gets the answer for the new payload.
    const prev = waiters.get(previousId);
    if (prev) {
      waiters.delete(previousId);
      for (const fn of prev) addWaiter(id, fn);
    }
  }
  mirror.set(req.key, id);
  notify();
  void flushOutbox();
  return outcome;
}

/**
 * No local database (e.g. storage blocked): one direct attempt, nothing is kept. A network failure
 * is then a rejection (the write is NOT queued), so the caller tells the user it was not saved.
 */
async function sendDirect(id: string, userId: string, req: EnqueueRequest): Promise<EnqueueOutcome> {
  const item: OutboxItem = { id, userId, kind: req.kind, key: req.key, method: req.method, path: req.path, body: req.body, createdAt: Date.now(), attempts: 0, failed: null };
  const h = handlers.get(req.kind);
  try {
    const response = await api<unknown>(req.path, { method: req.method, body: h?.prepare ? h.prepare(item) : req.body });
    h?.onSent?.(item, response);
    return { status: 'sent', response };
  } catch (e) {
    h?.onRejected?.(item, e, true);
    return { status: 'rejected', error: e };
  }
}

/** Drops a queued write (e.g. the user discarded a comment). */
export async function removeQueued(key: string): Promise<void> {
  const userId = currentUserId();
  if (!userId) return;
  const db = localDb();
  const rows = await withReopen(db, () => db.outbox.where('key').equals(key).toArray());
  const mine = rows.filter((r) => r.userId === userId && r.seq !== undefined);
  await withReopen(db, () => db.outbox.bulkDelete(mine.map((r) => r.seq!)));
  for (const r of mine) resolveWaiters(r.id, { status: 'queued' });
  await refreshMirror(userId);
}

/** Deletes the item unless it was collapsed (new op id) while it was being sent. */
async function removeIfSame(item: OutboxItem): Promise<void> {
  const db = localDb();
  await withReopen(db, () =>
    db.transaction('rw', db.outbox, async () => {
      const row = await db.outbox.get(item.seq!);
      if (row && row.id === item.id) await db.outbox.delete(item.seq!);
    }),
  );
}

async function bumpAttempts(item: OutboxItem, error: unknown): Promise<void> {
  const db = localDb();
  const status = (error as { status?: number })?.status ?? 0;
  const code = (error as { code?: string })?.code ?? 'network';
  await withReopen(db, () =>
    db.transaction('rw', db.outbox, async () => {
      const row = await db.outbox.get(item.seq!);
      if (row && row.id === item.id) await db.outbox.update(item.seq!, { attempts: row.attempts + 1, failed: { status, code, at: Date.now() } });
    }),
  );
}

function scheduleRetry(): void {
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = setTimeout(() => {
    retryTimer = null;
    if (navigator.onLine && document.visibilityState === 'visible') void flushOutbox();
  }, retryDelayMs(failures));
}

async function runOnce(): Promise<void> {
  const userId = currentUserId();
  if (!userId || !navigator.onLine) {
    resolveAllQueued();
    return;
  }
  const kinds = new Set(handlers.keys());
  for (;;) {
    let item: OutboxItem | undefined;
    try {
      item = flushOrder(await userItems(userId), userId, kinds)[0];
    } catch {
      resolveAllQueued();
      return;
    }
    if (!item || currentUserId() !== userId) break;
    const h = handlers.get(item.kind)!;
    let response: unknown;
    try {
      response = await api<unknown>(item.path, { method: item.method, body: h.prepare ? h.prepare(item) : item.body });
    } catch (e) {
      const action = classifyFailure(e);
      if (action !== 'drop') {
        if (action === 'retry') {
          failures++;
          await bumpAttempts(item, e).catch(() => undefined);
          scheduleRetry();
        }
        resolveAllQueued();
        await refreshMirror(userId);
        return;
      }
      const immediate = waiters.has(item.id);
      await removeIfSame(item).catch(() => undefined);
      unmirror(item);
      resolveWaiters(item.id, { status: 'rejected', error: e });
      try {
        h.onRejected?.(item, e, immediate);
      } catch (err) {
        console.warn('outbox onRejected failed', err);
      }
      await refreshMirror(userId);
      continue;
    }
    failures = 0;
    await removeIfSame(item).catch(() => undefined);
    unmirror(item);
    resolveWaiters(item.id, { status: 'sent', response });
    try {
      h.onSent?.(item, response);
    } catch (err) {
      console.warn('outbox onSent failed', err);
    }
    await refreshMirror(userId);
  }
}

/** Sends the signed-in user's queued writes in order (one flush at a time). */
export function flushOutbox(): Promise<void> {
  if (flushing) {
    again = true;
    return flushing;
  }
  flushing = (async () => {
    do {
      again = false;
      await runOnce(); // offline → resolves every waiter as 'queued' and returns at once
    } while (again);
  })().finally(() => {
    flushing = null;
  });
  return flushing;
}

/** Number of the user's writes still waiting (registered kinds). */
export async function pendingOutboxCount(userId: string): Promise<number> {
  try {
    return flushOrder(await userItems(userId), userId, new Set(handlers.keys())).length;
  } catch {
    return 0;
  }
}

/** Starts the triggers (idempotent) and re-applies queued state; call once a user is signed in. */
export function startOutbox(): void {
  if (!started) {
    started = true;
    window.addEventListener('online', () => void flushOutbox());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && navigator.onLine) void flushOutbox();
    });
    setInterval(() => {
      if (navigator.onLine && document.visibilityState === 'visible' && mirror.size > 0) void flushOutbox();
    }, 60_000);
  }
  void restore().then(() => flushOutbox());
}

/** Forgets in-memory state (logout / user switch wipes the DB itself), including the features' optimistic state. */
export function resetOutboxMemory(): void {
  resolveAllQueued();
  mirror = new Map();
  restored.clear();
  failures = 0;
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  for (const h of handlers.values()) {
    try {
      h.onReset?.();
    } catch (e) {
      console.warn('outbox reset failed', e);
    }
  }
  notify();
}
